import { useEffect, useRef } from "react"
import { isEqual } from "lodash"
import type { DashboardState } from "model/state"
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/redux"
import type { SelectedInstrument } from "helpers/types/instruments/selected"
import {
  getDashboardTradesKey,
  getTradeStorageScope,
  isSameTradeOwner,
  readSimulatorTrades,
  removeSimulatorTrades,
  subscribeTradeMirror,
  type MirroredStore,
  type StoredSimulatorTrades,
  type TradeStorageScope,
} from "helpers/simulator-trade-storage"
import { useDashboardId } from "../context/dashboard-id"
import { tradeSlice, type TradeItem } from "../store/trade-slice"

export interface DashboardTradeContext {
  [field: string]: string | number | null
  userId: string | null
  accountId: string | null
  dashboardId: string
  instrumentId: string | number | null
}

type StoredDashboardTrades = StoredSimulatorTrades<TradeItem, SelectedInstrument>

interface DashboardTradeState {
  dashboard: Pick<DashboardState, "core" | "model" | "trade">
}

function isTradeItem(value: unknown): value is TradeItem {
  return typeof value === "object" && value !== null && "field" in value && typeof value.field === "string"
}

function isSelectedInstrument(value: unknown): value is SelectedInstrument {
  return typeof value === "object" && value !== null && "type" in value && typeof value.type === "string" && "id" in value
}

export function readDashboardTrades(scope: TradeStorageScope, dashboardId: string): StoredDashboardTrades | null {
  const stored = readSimulatorTrades(getDashboardTradesKey(scope, dashboardId))
  if (!stored) return null
  const { context, trades, actionsBase } = stored
  if (!trades.every(isTradeItem)) return null
  if (actionsBase !== null && !isSelectedInstrument(actionsBase)) return null
  return { context, trades, actionsBase }
}

export function getDashboardTradeContext(state: DashboardTradeState, dashboardId: string): DashboardTradeContext {
  const core = state.dashboard.core[dashboardId]
  const scope = getTradeStorageScope(state)
  return {
    userId: scope?.userId ?? null,
    accountId: scope?.accountId ?? null,
    dashboardId,
    instrumentId: core?.instrument?.id ?? null,
  }
}

export function isDashboardTradeContextSettled(current: DashboardTradeContext, isModelLoaded: boolean): boolean {
  return isModelLoaded && current.instrumentId !== null
}

const OWNING_CONTEXT_FIELDS = ["userId", "accountId", "dashboardId", "instrumentId"] as const

export function subscribeDashboardTradeMirror(store: MirroredStore<DashboardTradeState>, dashboardId: string): () => void {
  return subscribeTradeMirror(store, {
    selectTrades: state => state.dashboard.trade[dashboardId]?.trades,
    selectActionsBase: state => state.dashboard.trade[dashboardId]?.actionsBase ?? null,
    selectContext: state => getDashboardTradeContext(state, dashboardId),
    getKey: ({ userId, accountId }) => (userId && accountId ? getDashboardTradesKey({ userId, accountId }, dashboardId) : null),
    owningFields: OWNING_CONTEXT_FIELDS,
  })
}

export function useTradeStorageSync(): void {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const context = useAppSelector((s) => getDashboardTradeContext(s, dashboardId), isEqual)
  const arePreferencesLoaded = useAppSelector((s) => (
    !!s.dashboard.core[dashboardId]?.isPreferencesLoaded && !!s.dashboard.model[dashboardId]?.isPreferencesLoaded
  ))
  const isModelLoaded = useAppSelector((s) => !!s.dashboard.model[dashboardId]?.modelId)
  const scope = useAppSelector(getTradeStorageScope, isEqual)
  const pendingRef = useRef<{ key: string, entry: StoredDashboardTrades | null } | null>(null)

  useEffect(() => subscribeDashboardTradeMirror(store, dashboardId), [store, dashboardId])

  useEffect(() => {
    if (!scope) return
    const key = getDashboardTradesKey(scope, dashboardId)
    if (pendingRef.current?.key !== key) pendingRef.current = { key, entry: readDashboardTrades(scope, dashboardId) }
    const pending = pendingRef.current.entry
    if (!pending || !arePreferencesLoaded) return
    if (store.getState().dashboard.trade[dashboardId]?.trades?.length) {
      pendingRef.current = { key, entry: null }
      return
    }
    if (!isDashboardTradeContextSettled(context, isModelLoaded)) return
    pendingRef.current = { key, entry: null }
    if (!isSameTradeOwner(pending.context, context, OWNING_CONTEXT_FIELDS)) {
      removeSimulatorTrades(key)
      return
    }
    dispatch(tradeSlice.actions.setTrades({ dashboardId, trades: pending.trades, actionsBase: pending.actionsBase }))
  }, [arePreferencesLoaded, isModelLoaded, scope, context, store, dashboardId, dispatch])
}
